import { LowerCasePipe } from '@angular/common';
import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

import { QRCodeModule } from 'angularx-qrcode';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { SEARCH_DEBOUNCE_TIME } from '../../constants';
import { LabelCodeListenerDirective } from '../../directives/label-code-listener.directive';
import { LabelCode, LabelCodeType, LabelCodeTypeMap } from '../../models/label-code.models';
import { ListAsWordsPipe } from '../../pipes/list-as-words.pipe';

@Component({
  selector: 'app-scan-qr-code',
  templateUrl: './scan-qr-code.component.html',
  styleUrl: './scan-qr-code.component.scss',
  standalone: true,
  imports: [
    LabelCodeListenerDirective,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    QRCodeModule,
    ReactiveFormsModule,
    LowerCasePipe,
    ListAsWordsPipe,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: ScanQrCodeComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: ScanQrCodeComponent,
    },
  ],
})
export class ScanQrCodeComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  @Input() allowManualScan: boolean = false;
  @Input() restrictType: LabelCodeType[] = [];
  @Input() showScannedDetails: boolean = true;

  labelCode: LabelCode = null;
  get mode(): ScanningMode {
    return this._mode;
  }

  disabled = false;

  manualFormGroup = this.formBuilder.group({
    manualEntry: [],
  });

  @Input() formControl: UntypedFormControl;
  @Input() formControlName: string;

  get control(): AbstractControl {
    return this.formControl || this.controlContainer.control.get(this.formControlName);
  }

  private _mode: ScanningMode = 'scan';
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private _onChange = (labelCode: LabelCode) => {};
  private _onTouched = () => {};
  private _onValidatorChange: () => void;
  private _subscriptions: Subscription[] = [];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private controlContainer: ControlContainer,
  ) {}

  ngOnInit(): void {
    this._subscriptions.push(
      (this.manualFormGroup.get('manualEntry').valueChanges as Observable<string>)
        .pipe(
          debounceTime(SEARCH_DEBOUNCE_TIME),
          distinctUntilChanged(),
          filter(() => this.mode === 'manual'),
        )
        .subscribe((x) => this.labelCodeEntered(x)),
    );
  }

  ngOnDestroy(): void {
    this._subscriptions?.forEach((s) => s.unsubscribe());
  }

  writeValue(labelCode: LabelCode | string): void {
    if (labelCode == null) {
      this.labelCode = undefined;
      this.manualFormGroup.get('manualEntry').reset();
      return;
    }

    this.labelCode =
      typeof labelCode === 'string'
        ? new LabelCode('Unknown', labelCode)
        : (this.labelCode = new LabelCode(labelCode.type, labelCode.identifier));
  }

  registerOnChange(fn: (labelCode: LabelCode) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(): ValidationErrors {
    if (
      this.mode === 'scan' &&
      this.labelCode &&
      this.restrictType.length &&
      !this.restrictType.includes(this.labelCode.type)
    ) {
      return {
        invalidType: {
          type: this.labelCode.type,
          typeName: LabelCodeTypeMap[this.labelCode.type].name,
          requiredTypes: this.restrictType,
          requiredTypeNames: this.restrictType.map((t) => LabelCodeTypeMap[t].name),
        },
      };
    }
  }

  registerOnValidatorChange?(fn: () => void): void {
    this._onValidatorChange = fn;
  }

  @HostListener('dblclick', [])
  onDblClick() {
    this.switchMode('manual', false);
  }

  switchMode(mode: ScanningMode, automatic: boolean) {
    this._onTouched();

    if (this._mode === mode || this.disabled) {
      return;
    }

    if (!automatic) {
      this.labelCode = undefined;
      this.manualFormGroup.get('manualEntry').reset();
      this._onChange(this.labelCode);
    }

    this._mode = mode;
    this._onValidatorChange();
  }

  labelCodeScanned(labelCode: LabelCode) {
    this._onTouched();

    if (this.disabled) {
      return;
    }

    this.switchMode('scan', true);
    this.labelCode = new LabelCode(labelCode.type, labelCode.identifier);
    this._onChange(this.labelCode);
  }

  labelCodeEntered(identifier: string) {
    this._onTouched();

    if (this.disabled) {
      return;
    }

    this.labelCode = identifier !== '' ? new LabelCode('Unknown', identifier) : undefined;
    this._onChange(this.labelCode);
  }
}

type ScanningMode = 'scan' | 'manual';
